
import { useApiStore } from "@/stores/apiStore";
import { useSiteStore } from "@/stores/siteStore";
import { useSsaStore } from "@/stores/ssaStore";
import { usePvwStore } from "@/stores/ssaStore/pvw";
import { useToastStore } from "@/stores/toastStore";
import { useViewStore } from "@/stores/ssaStore/views";
import { defineComponent } from "vue";
import { useActiveStore } from "@/stores/ssaStore/active";
import { useSaveStateStore } from "@/stores/ssaStore/saveState";
import { useDisplayPaneStore } from "@/stores/ssaStore/displayPane";
import { DatasetConfigs } from "@/utils/DisplayPaneDefaults";
import { storeToRefs } from "pinia";
import { useHierarchyStore } from "@/stores/hierarchyStore";
import { DatasetType, IParaviewColumnMapping } from "@/types/dataset";
import {
	BLASTDOG_DATASET_NAME,
	BlockSortOrder,
	IOGAS_GROUP_NAME,
	NonDrillholeLayerConfigType
} from "@/utils/DisplayPaneConstants";
import { useConfigStore } from "@/stores/configStore";
import { usePresetsStore } from "@/stores/ssaStore/presets";
import vtkMath from "vtk.js/Sources/Common/Core/Math";
import { FireWindowClick } from "@/utils/Utilities";

import { LayerNameToColumnLayerIdentifier } from "@/utils/DisplayPane";
import { useFiltersStore } from "@/stores/ssaStore/filters";

export default defineComponent({
	name: "imdex-display-pane",
	setup() {
		const api = useApiStore();
		const siteStore = useSiteStore();
		const pvwStore = usePvwStore();
		const { PVW_FILTER_SLICE_DISABLE: disablePvwSlice } = usePvwStore();
		const ssaStore = useSsaStore();
		const viewStore = useViewStore();
		const toast = useToastStore();
		const activeStore = useActiveStore();
		const displayPaneStore = useDisplayPaneStore();
		const { displayStates } = storeToRefs(useDisplayPaneStore());
		const { SSA_SET_APPLIED_DEFAULT: set_applied_default } = useDisplayPaneStore();
		const hierarchyStore = useHierarchyStore();
		const { HIERARCHY_STATE: hierarchyState } = storeToRefs(useHierarchyStore());
		const { hierarchyLayerSelections } = storeToRefs(useHierarchyStore());
		const {
			view: activeView,
			SSA_ACTIVE_DATASET: activeDataset,
			dataset: datasets
		} = storeToRefs(useActiveStore());
		const { config } = useConfigStore();
		const { loadingState } = storeToRefs(useSaveStateStore());
		const presetStore = usePresetsStore();
		const fireWindowClick = FireWindowClick;
		const {
			SSA_FILTER_STATE_OPEN: openFilters,
			SSA_FILTER_STATE_SELECTED: selectFilters,
			SSA_ALL_ACTIVE_FILTERS_STATE: filtersState,
			filtersIconClickedCount
		} = storeToRefs(useFiltersStore());
		const filtersStore = useFiltersStore();

		return {
			presetStore,
			set_applied_default,
			hierarchyState,
			hierarchyStore,
			activeStore,
			activeDataset,
			viewStore,
			activeView,
			hierarchyLayerSelections,
			api,
			siteStore,
			pvwStore,
			ssaStore,
			toast,
			datasets,
			displayPaneStore,
			displayStates,
			blastdog_defaults: (config as any).blastdog_defaults,
			loadingState,
			fireWindowClick,
			openFilters,
			selectFilters,
			filtersIconClickedCount,
			filtersStore,
			disablePvwSlice,
			filtersState
		};
	},
	data() {
		return {
			isLoading: false,
			Drillhole: require("@/assets/svg/hole_type2.svg"),
			Blockmodel: require("@/assets/svg/bm_type.svg"),
			Surface: require("@/assets/svg/shape.svg"),
			Points: require("@/assets/svg/polygon_type.svg"),
			ioGAS: require("@/assets/svg/ioGAS_web.svg"),
			openFiltersTmp: false,
			selectFiltersTmp: false,
			isFiltersDisabled: false
		};
	},
	created() {
		const isAnyLayerSelected = this.displayMethods.find(
			(layer: any) => layer.selected && layer.name != "Slicing"
		);

		this.selectFiltersTmp = this.selectFilters;
		this.openFiltersTmp = this.openFilters;

		// MPD-3754: tbc if this is desired behaviour
		if (!isAnyLayerSelected) {
			this.isFiltersDisabled = true;
		}
	},
	methods: {
		runOnChangeFunction(v: any, configOption: any) {
			if (configOption && configOption.name === "showLabel" && !configOption.onChange) {
				const activeView = this.activeStore.view;
				// const selectedProperty = displayPaneState[activeView][activeDatasetId].find((item: any) => {
				// 	return item.selected && item.open;
				// }).configuration[0].selectedProperty;

				// if (selectedProperty) {
				// 	ssa().SSA_REPRESENTATION_UPDATE_PROPERTY({
				// 		layerName: active().SSA_ACTIVE_LAYER,
				// 		viewId: activeView,
				// 		type: "labels",
				// 		name: "CellFieldDataArrayName",
				// 		value: selectedProperty.value
				// 	});
				// }

				this.ssaStore.SSA_REPRESENTATION_UPDATE_PROPERTY({
					layerName: this.activeStore.SSA_ACTIVE_LAYER,
					viewId: activeView,
					type: "labels",
					name: "Visibility",
					value: v ? 1 : 0
				});
			} else {
				configOption.onChange(v);
			}
		},
		applyDefaultColor(layerName: string, color: { r: number; g: number; b: number }) {
			this.ssaStore.SSA_REPRESENTATION_UPDATE_PROPERTY({
				layerName,
				viewId: this.activeStore.view,
				type: "geometry",
				name: "DiffuseColor",
				value: [color.r / 255, color.g / 255, color.b / 255]
			});
			this.ssaStore.SSA_REPRESENTATION_UPDATE_PROPERTY({
				layerName,
				viewId: this.activeStore.view,
				type: "geometry",
				name: "Visibility",
				value: 1
			});
		},
		applyDefaultPointSize(layerName: string, point_size: number) {
			this.ssaStore.SSA_REPRESENTATION_UPDATE_PROPERTY({
				layerName,
				viewId: this.activeStore.view,
				type: "geometry",
				name: "PointSize",
				value: point_size
			});
			this.ssaStore.SSA_REPRESENTATION_UPDATE_PROPERTY({
				layerName,
				viewId: this.activeStore.view,
				type: "geometry",
				name: "Ambient",
				value: 0
			});
		},
		applyDefaultlineWidth(layerName: string, lineWidth: number) {
			this.ssaStore.SSA_REPRESENTATION_UPDATE_PROPERTY({
				layerName,
				viewId: this.activeStore.view,
				type: "geometry",
				name: "LineWidth",
				value: lineWidth
			});
			this.ssaStore.SSA_REPRESENTATION_UPDATE_PROPERTY({
				layerName,
				viewId: this.activeStore.view,
				type: "geometry",
				name: "Ambient",
				value: 0
			});
			this.ssaStore.SSA_REPRESENTATION_UPDATE_PROPERTY({
				layerName,
				viewId: this.activeStore.view,
				type: "geometry",
				name: "Visibility",
				value: 1
			});
		},
		async applyDefaultPickColor(layerName: string, field: string, color: any) {
			const componentToHex = (c: any) => {
				const hex = c.toString(16);
				return hex.length == 1 ? "0" + hex : hex;
			};
			const rgbToHex = (r: number, g: number, b: number) => {
				return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
			};
			const rgbPointsToEdit = [];
			const n = 0;
			rgbPointsToEdit[n] = 0;
			rgbPointsToEdit[n + 1] = vtkMath.hex2float(rgbToHex(color.r, color.g, color.b))[0];
			rgbPointsToEdit[n + 2] = vtkMath.hex2float(rgbToHex(color.r, color.g, color.b))[1];
			rgbPointsToEdit[n + 3] = vtkMath.hex2float(rgbToHex(color.r, color.g, color.b))[2];
			rgbPointsToEdit[n + 4] = 1;
			rgbPointsToEdit[n + 5] = vtkMath.hex2float(rgbToHex(color.r, color.g, color.b))[0];
			rgbPointsToEdit[n + 6] = vtkMath.hex2float(rgbToHex(color.r, color.g, color.b))[1];
			rgbPointsToEdit[n + 7] = vtkMath.hex2float(rgbToHex(color.r, color.g, color.b))[2];

			const stateToPush = {
				layerName,
				viewId: this.activeStore.view,
				field,
				column: this.presetStore.SSA_FIELD_COLUMN_GET(field),
				rgbPoints: rgbPointsToEdit
			};
			await this.ssaStore.SSA_COLOR_BY_UPDATE(stateToPush);
		},
		sortOnBlockSortOrder(a: any, b: any) {
			const indexA = BlockSortOrder.indexOf(a.layerBlockName);
			const indexB = BlockSortOrder.indexOf(b.layerBlockName);
			return indexA - indexB;
		},
		activateLayer(item: any | string) {
			// item is a string when we pass only layer name from threshold filtering
			if (typeof item == "string") {
				item = this.displayMethods.find(
					(displayMethod: any) => displayMethod.layerName == item
				);

				if (!item || typeof item == "string") return;
				if (!item.open) item.open = true; // we don't want to open and close these on filter creation, but we need to open it on initial activation
			} else {
				item.open = !item.open;
			}

			this.activeStore.SSA_ACTIVE_LAYER_SET(item.layerName);
			const layerName = item.layerName;
			const viewId = this.activeStore.view;
			let fieldList: any = ["hole_name", "bench_name", "pattern_name"];

			if (
				item.configuration.length > 0 &&
				item.configuration[0]?.availableProperties?.length > 0
			) {
				fieldList = fieldList.concat(
					item.configuration[0].availableProperties[0].children.map((item: any) => {
						return item.value;
					})
				);
			}

			if (item.configuration.length) {
				const showLabelConfigIndex = item.configuration.findIndex(
					(config: any) => config.name === "showLabel"
				);

				if (showLabelConfigIndex > -1) {
					const showLabelConfig = item.configuration[showLabelConfigIndex];

					this.runOnChangeFunction(showLabelConfig.value, showLabelConfig);
				}
			}

			if (
				!item.name.toLowerCase().includes("survey") &&
				!item.name.toLowerCase().includes("slic")
			) {
				this.pvwStore.PVW_SET_FIELD_LIST({ layerName, viewId, fieldList });
			}

			this.displayMethods.forEach((item2: any) => {
				if (item.open) {
					item2.open = item2.layerName == this.activeStore.SSA_ACTIVE_LAYER;
				}
			});
		},

		async loadParaviewData(item: any) {
			const hierarchyLayerState =
				this.hierarchyLayerSelections[this.activeView] &&
				this.hierarchyLayerSelections[this.activeView][item.layerName];

			const obj = {} as { [key: string]: any };
			obj[item.layerName] = hierarchyLayerState;
			const activeLayers =
				this.displayMethods && this.displayMethods.filter((item: any) => item.open);

			if (activeLayers[0]) {
				activeLayers[0].open = false;
			}

			if (!item.selected) {
				item.open = false;
				this.activeStore.SSA_ACTIVE_LAYER_SET("");
				obj[item.layerName] = [];
			} else {
				this.activeStore.SSA_ACTIVE_LAYER_SET(item.layerName);
			}
			if (item.defaults_applied == null || !item.defaults_applied) {
				const defaultObj = item;

				if (defaultObj != null) {
					if (defaultObj.color) {
						this.$nextTick(() => {
							this.applyDefaultColor(item.layerName, defaultObj.color);
						});
						if (defaultObj.type == "picks") {
							this.$nextTick(() => {
								this.applyDefaultPickColor(
									item.layerName,
									item.pickProperty,
									defaultObj.color
								);
							});
						}
					}
				}

				if (item.selected) {
					this.$nextTick(() => {
						this.set_applied_default(item.layerName, this.hierarchyLayerSelections);
					});
				}
			}
			await this.pvwStore.PVW_LOAD_DATASET(this.activeView, obj);
		},
		selectFiltersClicked(v: boolean) {
			// in case we have called this from the displayMethods watcher
			if (this.selectFiltersTmp != v) {
				this.selectFiltersTmp = v;
			}

			// open filters if we have turned selection on.  Close if we have turned it off.
			if (this.openFiltersTmp != this.selectFiltersTmp) {
				this.setFiltersOpen(this.selectFiltersTmp);
			}

			// save filter selected state in the store
			this.filtersStore.SSA_SET_SELECTED_FILTER_STATE(this.selectFiltersTmp);
		},
		setFiltersOpen(v: boolean) {
			this.openFiltersTmp = v;
			this.filtersStore.SSA_SET_OPEN_FILTER_STATE(v);
		}
	},
	computed: {
		isBlastdog(): boolean {
			return this.activeDataset.groupName == BLASTDOG_DATASET_NAME;
		},
		isGAS(): boolean {
			// TODO we really should have a better way of figuring this out
			return this.activeDataset.groupName == IOGAS_GROUP_NAME || this.activeDataset.isGAS;
		},
		displayMethods(): any {
			return (
				(this.displayStates[this.activeView] &&
					this.datasets[this.activeView] &&
					this.displayStates[this.activeView][this.datasets[this.activeView].id]) ||
				[]
			);
		},
		hasDataLoaded(): any {
			return (
				this.activeDataset &&
				this.hierarchyState[this.activeView] &&
				this.hierarchyState[this.activeView].selected.filter((item: any) =>
					item.includes(this.activeDataset.id)
				).length
			);
		},
		isAnyDataLayerSelected(): boolean {
			return this.displayMethods.find((item: any) => item.selected && item.name != "Slicing");
		},
		filtersLayerMap(): any {
			const resultObject: { [layerName: string]: boolean } = {};
			for (const filter of this.filtersStore.SSA_ALL_ACTIVE_FILTERS) {
				if (
					filter.layerName &&
					this.displayMethods.find(
						(item: any) => item.layerName == filter.layerName && item.selected === true
					) &&
					!(filter.disableButDisplayFilter || filter.disableButDisplayFilterSlashEye)
				) {
					resultObject[filter.layerName] = true;
				}
			}
			return resultObject;
		}
	},
	watch: {
		displayMethods: {
			deep: true,
			async handler(v) {
				if (this.activeDataset) {
					const isAnyLayerSelected = this.isAnyDataLayerSelected;
					const slicingLayer = v.find((layer: any) => layer.name === "Slicing");

					// We only want to run this code if layers exist, but none of them are selected
					if (v.length && !isAnyLayerSelected) {
						if (slicingLayer) {
							const layerNames = Object.keys(
								this.activeDataset.paraviewLayersInfoSplit
							);
							for (let i = 0; i < layerNames.length; i++) {
								await this.disablePvwSlice({
									layerName: layerNames[i],
									viewId: this.activeView
								});
							}

							slicingLayer.selected = false;
							slicingLayer.disabled = true;
						}

						// MPD-3754 TBC - we want to turn filters off if no layers are selected
						if (this.selectFiltersTmp) {
							this.selectFiltersClicked(false);
						}
					} else if (isAnyLayerSelected) {
						// If any display pane layers are selected, turn off the disabled flag for slicing
						if (slicingLayer) {
							slicingLayer.disabled = false;
						}
					}

					// ... and do the same for filters
					this.isFiltersDisabled = !isAnyLayerSelected;
				}
			}
		},
		activeDataset: {
			deep: true,
			handler() {
				// Reload component state from the store when the active dataset changes
				this.openFiltersTmp = this.openFilters;
				this.selectFiltersTmp = this.selectFilters;
				this.$nextTick(() => {
					const activeLayers =
						this.displayMethods && this.displayMethods.filter((item: any) => item.open);
					if (activeLayers[0]) {
						if (this.hasDataLoaded) {
							this.activeStore.SSA_ACTIVE_LAYER_SET(activeLayers[0].layerName);
						} else {
							this.activeStore.SSA_ACTIVE_LAYER_SET("");
						}
					}
				});
			}
		},
		hierarchyLayerSelections: {
			deep: true,
			handler(v) {
				this.$nextTick(() => {
					const activeLayers =
						this.displayMethods && this.displayMethods.filter((item: any) => item.open);

					if (activeLayers[0]) {
						if (this.hasDataLoaded) {
							this.activeStore.SSA_ACTIVE_LAYER_SET(activeLayers[0].layerName);
						} else {
							activeLayers[0].open = false;
							this.activeStore.SSA_ACTIVE_LAYER_SET("");
						}
					}
				});
				this.$nextTick(async () => {
					const activeLayers =
						this.displayMethods &&
						this.displayMethods.filter((item: any) => item.selected);

					const obj = {} as { [key: string]: any };

					for (let i = 0; i < activeLayers.length; i++) {
						if (
							activeLayers[i].defaults_applied == null ||
							!activeLayers[i].defaults_applied
						) {
							const defaultObj = activeLayers[i];

							if (defaultObj != null) {
								if (defaultObj.color) {
									this.$nextTick(() => {
										this.applyDefaultColor(
											activeLayers[i].layerName,
											defaultObj.color
										);
									});
									if (defaultObj.type == "picks") {
										this.$nextTick(() => {
											this.applyDefaultPickColor(
												activeLayers[i].layerName,
												activeLayers[i].pickProperty,
												defaultObj.color
											);
										});
									}
								}
							}
							if (activeLayers[i].selected) {
								this.set_applied_default(activeLayers[i].layerName, v);
							}
						} else {
							if (activeLayers[i].selected) {
								this.set_applied_default(activeLayers[i].layerName);
							}
						}

						obj[activeLayers[i].layerName] =
							v[this.activeView][activeLayers[i].layerName];
					}
					await this.pvwStore.PVW_LOAD_DATASET(this.activeView, obj);
					for (let i = 0; i < activeLayers.length; i++) {
						if (
							activeLayers[i].configuration != null &&
							activeLayers[i].configuration.length > 0
						) {
							activeLayers[i].configuration[0].onLoad &&
								activeLayers[i].configuration[0].onLoad();
						}
					}
				});
			}
		},
		datasets: {
			deep: true,
			handler(v) {
				if (this.loadingState) return;
				if (v[this.activeView] == null) return;
				if (
					this.displayStates[this.activeView] == null ||
					(this.displayStates[this.activeView] &&
						(this.displayStates[this.activeView][v[this.activeView].id] == null ||
							!this.displayStates[this.activeView][v[this.activeView].id].length))
				) {
					const typeMap = {
						Drillhole: "drillhole",
						Blockmodel: "blockmodel",
						Surface: "surface",
						Points: "points"
					};
					const Defaults = new DatasetConfigs();
					const ddefault = this.isBlastdog
						? Defaults.blastdog_drillhole
						: Defaults[
								typeMap[
									v[this.activeView].dataType as keyof typeof typeMap
								] as keyof DatasetConfigs
						  ];
					const datasetType = v[this.activeView].dataType;

					const activeDatasetParaviewLayersInfo =
						v[this.activeView].paraviewLayersInfoSplit;
					const tempConfig = [] as any[];

					if (activeDatasetParaviewLayersInfo) {
						const layerIdentifiers = Object.keys(activeDatasetParaviewLayersInfo);

						for (const datas of layerIdentifiers) {
							let layerBlockName =
								activeDatasetParaviewLayersInfo[datas].displayPaneLabel;

							const layerConfigType =
								activeDatasetParaviewLayersInfo[datas].layerConfigType;
							const layerPathSplit = datas.split("/");
							const fileName = layerPathSplit.pop();
							let layerIdentifier = fileName;
							if (!layerIdentifier) return;
							if (
								layerConfigType == NonDrillholeLayerConfigType &&
								datasetType == "Blockmodel"
							)
								layerBlockName = "Blocks";
							else if (
								layerConfigType == NonDrillholeLayerConfigType &&
								datasetType == "Surface"
							)
								layerBlockName = "Surface";
							else if (
								layerConfigType == NonDrillholeLayerConfigType &&
								datasetType == "Points"
							)
								layerBlockName = "Points";
							else if (datasetType == "Drillhole") {
								// we need to add the purpose of the file as we are now allowing duplicates, and this enforces uniqueness even for duplicate filenames
								// the conditional is to support legacy blobName paths, which didn't include 'collar', 'survey', 'points0' etc.

								layerIdentifier = LayerNameToColumnLayerIdentifier(
									layerPathSplit,
									layerIdentifier
								);
							}

							const layerDefaultOptions = {
								...ddefault[
									ddefault.findIndex((item) => {
										if (layerConfigType == NonDrillholeLayerConfigType)
											return true; // return the first one for default; layerConfigType will need more values to support flexible display panes for blockmodels/points/surfaces
										else
											return (
												item.name.toLowerCase() ==
													layerBlockName.toLowerCase() ||
												item.name == layerConfigType
											);
									})
								]
							} as unknown as DatasetConfigs & {
								name?: string;
								visible?: boolean;
								layerBlockName?: string;
								layerName?: string;
								layerIdentifier?: string;
								fileName?: string;
								pickProperty?: string;
								default_properties: { value: string; label: string }[];
								selected?: boolean;
								open?: boolean;
							};

							if (activeDatasetParaviewLayersInfo[datas].pickProperty) {
								layerDefaultOptions.pickProperty =
									activeDatasetParaviewLayersInfo[datas].pickProperty;
							}

							const defaultLayerSettings = v[this.activeView].defaults?.find(
								(defaultLayerSetting: {
									layerIdentifier: string;
									colorByProperty?: string;
									selected?: boolean;
								}) => defaultLayerSetting.layerIdentifier === layerIdentifier
							);
							if (defaultLayerSettings) {
								if (defaultLayerSettings.colorByProperty) {
									layerDefaultOptions.default_properties = [
										{
											value: defaultLayerSettings.colorByProperty,
											label: defaultLayerSettings.colorByProperty
										}
									];
								}
								if (typeof defaultLayerSettings.selected === "boolean") {
									layerDefaultOptions.selected = defaultLayerSettings.selected;
								}
								if (typeof defaultLayerSettings.open === "boolean") {
									layerDefaultOptions.open = defaultLayerSettings.selected;
								}
							}

							layerDefaultOptions.name = layerBlockName;
							layerDefaultOptions.visible = true;
							layerDefaultOptions.layerBlockName = layerBlockName;
							layerDefaultOptions.layerName = datas;
							layerDefaultOptions.fileName = fileName;
							layerDefaultOptions.layerIdentifier = layerIdentifier;
							tempConfig.push(layerDefaultOptions);
						}
					}
					if (datasetType == "Drillhole") {
						for (let i = 0; i < tempConfig.length; i++) {
							if (tempConfig[i].configuration != null) {
								if (tempConfig[i].configuration.length) {
									const parsedConfig: any = [];

									tempConfig[i].configuration.forEach((config: any) => {
										const shallowCopyOfTempConfigObject = config;

										// extract functions from the shallow copy to add to the deep clone
										// because json parse/stringify will remove them
										const functionProperties: any = {};
										Object.keys(shallowCopyOfTempConfigObject).forEach(
											(key) => {
												if (
													typeof shallowCopyOfTempConfigObject[key] ===
													"function"
												) {
													functionProperties[key] =
														shallowCopyOfTempConfigObject[key];
												}
											}
										);

										const parsedConfigObject = JSON.parse(
											JSON.stringify(config)
										);

										// Add back functions that may have been stripped out
										for (const property in functionProperties) {
											parsedConfigObject[property] =
												functionProperties[property];
										}

										// copy object to prevent a re-used reference
										parsedConfig.push(parsedConfigObject);
									});

									tempConfig[i].configuration = parsedConfig;
								} else {
									tempConfig[i].configuration = [];
								}

								if (tempConfig[i].pickProperty) {
									// pass the pickProp for this layer down to the config for this display component
									tempConfig[i].configuration[0].pickProperty =
										tempConfig[i].pickProperty;
								}
								// create column/property config from columns
								if (tempConfig[i].configuration[0]) {
									tempConfig[i].configuration[0].availableProperties = [
										{
											label: this.$t(`ssa.displayPane.propertiesLabel`),
											value: "default_props",
											children: [
												...v[this.activeView].datasetColumnMapping
													.filter((item: IParaviewColumnMapping) => {
														if (
															item.layerIdentifier &&
															tempConfig[i] != null
														) {
															return (
																item.layerIdentifier.toLowerCase() ==
																tempConfig[
																	i
																].layerIdentifier?.toLowerCase()
															);
														}
													})
													.sort(
														(
															a: IParaviewColumnMapping,
															b: IParaviewColumnMapping
														) => {
															const aOrder =
																a.displayOrder ??
																Number.MAX_SAFE_INTEGER;
															const bOrder =
																b.displayOrder ??
																Number.MAX_SAFE_INTEGER;
															return aOrder - bOrder;
														}
													)
													.map((item: IParaviewColumnMapping) => {
														let type = "continuous";
														if (item.isCategorical == "true")
															type = "categorical";
														// if (item.column == tempConfig[i].pickProperty)
														// 	type = "categorical";
														return {
															label: item.label,
															value: item.column,
															isTimeStamp: item.isTimeStamp,
															type
														};
													})
											]
										}
									];
								}
							}
						}
					}
					if (datasetType == "Blockmodel") {
						const dd = typeMap[v[this.activeView].dataType as keyof typeof typeMap];
						if (dd == "blockmodel" && ddefault.length > 1) {
							for (let i = 1; i < ddefault.length; i++) {
								tempConfig.push(ddefault[i]);
							}
						}

						if (
							tempConfig[tempConfig.findIndex((item) => item.name == "Blocks")] !=
							null
						) {
							tempConfig[
								tempConfig.findIndex((item) => item.name == "Slicing")
							].layerName =
								tempConfig[tempConfig.findIndex((item) => item.name == "Blocks")]
									.layerName + "__slice";
							tempConfig[
								tempConfig.findIndex((item) => item.name == "Blocks")
							].configuration[0].availableProperties = [
								{
									label: this.$t(`ssa.displayPane.propertiesLabel`),
									value: "default_props",
									children: [
										...v[this.activeView].datasetColumnMapping
											.filter((item: IParaviewColumnMapping) => {
												if (
													item.layerIdentifier &&
													tempConfig[
														tempConfig.findIndex(
															(item) => item.name == "Blocks"
														)
													] != null
												) {
													return (
														item.layerIdentifier.toLowerCase() ==
														tempConfig[
															tempConfig.findIndex(
																(item) => item.name == "Blocks"
															)
														].layerIdentifier?.toLowerCase()
													);
												}
											})
											.sort(
												(
													a: IParaviewColumnMapping,
													b: IParaviewColumnMapping
												) => {
													const aOrder =
														a.displayOrder ?? Number.MAX_SAFE_INTEGER;
													const bOrder =
														b.displayOrder ?? Number.MAX_SAFE_INTEGER;
													return aOrder - bOrder;
												}
											)
											.map((item: IParaviewColumnMapping) => {
												return {
													label: item.label,
													value: item.column,
													isTimeStamp: item.isTimeStamp,
													type:
														item.isCategorical == "true"
															? "categorical"
															: "continuous"
												};
											})
									]
								}
							];
						}
					}

					if (datasetType == "Points") {
						if (
							tempConfig[tempConfig.findIndex((item) => item.name == "Points")] !=
							null
						) {
							tempConfig[
								tempConfig.findIndex((item) => item.name == "Points")
							].configuration[0].availableProperties = [
								{
									label: this.$t(`ssa.displayPane.propertiesLabel`),
									value: "default_props",
									children: [
										...v[this.activeView].datasetColumnMapping
											.filter((item: IParaviewColumnMapping) => {
												return (
													item.layerIdentifier.toLowerCase() ==
													tempConfig[
														tempConfig.findIndex(
															(item) => item.name == "Points"
														)
													].layerIdentifier?.toLowerCase()
												);
											})
											.sort(
												(
													a: IParaviewColumnMapping,
													b: IParaviewColumnMapping
												) => {
													const aOrder =
														a.displayOrder ?? Number.MAX_SAFE_INTEGER;
													const bOrder =
														b.displayOrder ?? Number.MAX_SAFE_INTEGER;
													return aOrder - bOrder;
												}
											)
											.map((item: IParaviewColumnMapping) => {
												return {
													label: item.label,
													value: item.column,
													isTimeStamp: item.isTimeStamp,
													type:
														item.isCategorical == "true"
															? "categorical"
															: "continuous"
												};
											})
									]
								}
							];
						}
					}
					if (datasetType == "Surface") {
						if (
							tempConfig[tempConfig.findIndex((item) => item.name == "Surface")] !=
							null
						) {
							tempConfig[
								tempConfig.findIndex((item) => item.name == "Surface")
							].configuration[0].availableProperties = [
								{
									label: this.$t(`ssa.displayPane.propertiesLabel`),
									value: "default_props",
									children: [
										{
											label: "hierarchy_0",
											value: "hierarchy_0",
											type: "categorical"
										}
									]
								}
							];
						}
					}

					this.displayPaneStore.ADD_DATASET_DISPLAY_STATE({
						[v[this.activeView].id]: tempConfig
					});
				}
			}
		},
		filtersIconClickedCount: {
			handler() {
				this.openFiltersTmp = true;
			}
		}
	}
});
